// Tâches de l'équipe : les responsables créent et assignent ; chacun suit et met à jour les siennes.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem,
  Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline, EditOutlined, SendOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Panel } from '../../components/ui';
import { planningAPI } from '../../services/api';
import { apiError, dateFr, dateHeureFr } from '../../utils/format';
import { CATEGORIES_TACHE, PRIORITES, STATUTS_TACHE } from './common';

const vide = { titre: '', description: '', assigne_a: '', echeance: '', priorite: 'NORMALE', categorie: '' };

export function TacheDialog({ open, tache, onClose, onSaved }) {
  const [f, setF] = useState(vide);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError('');
    setF(tache ? { ...vide, ...Object.fromEntries(Object.keys(vide).map((k) => [k, tache[k] ?? ''])) } : vide);
    planningAPI.utilisateurs().then((r) => setUsers(r.data)).catch((e) => setError(apiError(e)));
  }, [open, tache]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = tache ? await planningAPI.modifierTache(tache.id, f) : await planningAPI.creerTache(f);
      onSaved(r.data.message);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{tache ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField fullWidth autoFocus label="Titre" value={f.titre} onChange={set('titre')} placeholder="ex. Nettoyer et huiler les moules 12 m" /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Description / consignes" value={f.description} onChange={set('description')} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Assignée à" value={f.assigne_a} onChange={set('assigne_a')}>
              <MenuItem value=""><em>Personne pour l'instant</em></MenuItem>
              {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.nom}{u.fonction ? ` — ${u.fonction}` : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Échéance" InputLabelProps={{ shrink: true }} value={f.echeance} onChange={set('echeance')} /></Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Priorité" value={f.priorite} onChange={set('priorite')}>
              {Object.entries(PRIORITES).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Catégorie" value={f.categorie} onChange={set('categorie')}>
              <MenuItem value=""><em>—</em></MenuItem>
              {CATEGORIES_TACHE.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 2 }}>La personne assignée est prévenue (cloche et email) et reçoit un rappel le jour de l'échéance puis tant que la tâche est en retard.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !f.titre.trim()} onClick={save}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}

export function TacheDetail({ id, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [texte, setTexte] = useState('');
  const [error, setError] = useState('');
  const [edit, setEdit] = useState(false);
  const charger = useCallback(() => planningAPI.tache(id).then((r) => setT(r.data)).catch((e) => setError(apiError(e))), [id]);
  useEffect(() => { setT(null); setError(''); setTexte(''); if (id) charger(); }, [id, charger]);
  const statut = async (s) => {
    setError('');
    try { await planningAPI.statutTache(id, s, texte); setTexte(''); await charger(); onChanged(); } catch (e) { setError(apiError(e)); }
  };
  const commenter = async () => {
    setError('');
    try { await planningAPI.commenter(id, texte); setTexte(''); await charger(); onChanged(); } catch (e) { setError(apiError(e)); }
  };
  const supprimer = async () => {
    if (!window.confirm(`Supprimer la tâche « ${t.titre} » ?`)) return;
    try { await planningAPI.supprimerTache(id); onChanged('Tâche supprimée'); onClose(); } catch (e) { setError(apiError(e)); }
  };
  const ouverte = t && ['A_FAIRE', 'EN_COURS'].includes(t.statut);
  return (
    <Dialog open={!!id} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>{t?.titre || 'Tâche'}</Box>
        {t?.droits?.modifier && <>
          <Tooltip title="Modifier"><IconButton onClick={() => setEdit(true)} aria-label="Modifier la tâche"><EditOutlined /></IconButton></Tooltip>
          <Tooltip title="Supprimer"><IconButton color="error" onClick={supprimer} aria-label="Supprimer la tâche"><DeleteOutline /></IconButton></Tooltip>
        </>}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {t && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" color={STATUTS_TACHE[t.statut].color} label={STATUTS_TACHE[t.statut].label} />
              <Chip size="small" variant="outlined" color={PRIORITES[t.priorite].color} label={`Priorité ${PRIORITES[t.priorite].label.toLowerCase()}`} />
              {t.categorie && <Chip size="small" variant="outlined" label={t.categorie} />}
              {t.en_retard && <Chip size="small" color="error" label="En retard" />}
            </Stack>
            <Typography sx={{ fontSize: 14 }}>Assignée à <b>{t.assigne_nom || 'personne'}</b> · échéance <b>{t.echeance ? dateFr(t.echeance) : '—'}</b></Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>Créée par {t.createur_nom || '—'} le {dateHeureFr(t.created_at)}{t.terminee_le ? ` · terminée le ${dateHeureFr(t.terminee_le)}` : ''}</Typography>
            {t.description && <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14, bgcolor: 'action.hover', p: 1.5, borderRadius: 1, mb: 2 }}>{t.description}</Typography>}
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Suivi</Typography>
            {t.commentaires.length ? t.commentaires.map((k) => (
              <Box key={k.id} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{k.auteur} · {dateHeureFr(k.created_at)}</Typography>
                <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{k.texte}</Typography>
              </Box>
            )) : <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Aucun commentaire.</Typography>}
            <TextField fullWidth multiline minRows={2} size="small" sx={{ mt: 1.5 }} label="Commentaire (facultatif avec un changement de statut)" value={texte} onChange={(e) => setTexte(e.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<SendOutlined />} disabled={!texte.trim()} onClick={commenter}>Commenter</Button>
        <Box sx={{ flex: 1 }} />
        {t?.droits?.statut && t.statut === 'A_FAIRE' && <Button onClick={() => statut('EN_COURS')}>Commencer</Button>}
        {t?.droits?.statut && ouverte && <Button variant="contained" color="success" onClick={() => statut('TERMINEE')}>Marquer terminée</Button>}
        {t?.droits?.statut && !ouverte && <Button onClick={() => statut('A_FAIRE')}>Rouvrir</Button>}
        {t?.droits?.modifier && ouverte && <Button color="inherit" onClick={() => statut('ANNULEE')}>Annuler la tâche</Button>}
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
      <TacheDialog open={edit} tache={t} onClose={() => setEdit(false)} onSaved={(m) => { setEdit(false); charger(); onChanged(m); }} />
    </Dialog>
  );
}

const Taches = ({ id, onInfo, refreshResume }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [filtre, setFiltre] = useState('OUVERTES');
  const [mes, setMes] = useState(false);
  const [creer, setCreer] = useState(false);
  const [error, setError] = useState('');
  const charger = useCallback(() => planningAPI.taches({ statut: filtre || undefined, mes: mes ? 1 : undefined })
    .then((r) => setD(r.data)).catch((e) => setError(apiError(e))), [filtre, mes]);
  useEffect(() => { charger(); }, [charger]);
  const changed = (m) => { charger(); refreshResume(); if (m) onInfo(m); };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid', borderColor: 'divider', pr: 1.5 }}>
          <Tabs value={filtre} onChange={(e, v) => setFiltre(v)} variant="scrollable" sx={{ px: 1, flex: '1 1 auto' }}>
            <Tab value="OUVERTES" label="En cours" /><Tab value="TERMINEE" label="Terminées" /><Tab value="ANNULEE" label="Annulées" /><Tab value="" label="Toutes" />
          </Tabs>
          {d?.responsable && <Chip label="Mes tâches seulement" color={mes ? 'primary' : 'default'} variant={mes ? 'filled' : 'outlined'} onClick={() => setMes(!mes)} />}
          {d?.responsable && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setCreer(true)}>Nouvelle tâche</Button>}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>Tâche</TableCell><TableCell>Assignée à</TableCell><TableCell>Échéance</TableCell><TableCell>Priorité</TableCell><TableCell>Statut</TableCell></TableRow></TableHead>
            <TableBody>
              {d?.taches.map((t) => (
                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/planification/taches/${t.id}`)}>
                  <TableCell><b>{t.titre}</b>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{[t.categorie, t.nb_commentaires ? `${t.nb_commentaires} commentaire${t.nb_commentaires > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ')}</Typography></TableCell>
                  <TableCell>{t.assigne_nom || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: t.en_retard ? 'error.main' : undefined, fontWeight: t.en_retard ? 700 : undefined }}>{t.echeance ? dateFr(t.echeance) : '—'}{t.en_retard ? ' · en retard' : ''}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={PRIORITES[t.priorite].color} label={PRIORITES[t.priorite].label} /></TableCell>
                  <TableCell><Chip size="small" color={STATUTS_TACHE[t.statut].color} label={STATUTS_TACHE[t.statut].label} /></TableCell>
                </TableRow>
              ))}
              {d && !d.taches.length && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>Aucune tâche{filtre === 'OUVERTES' ? ' en cours' : ''}.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <TacheDialog open={creer} onClose={() => setCreer(false)} onSaved={(m) => { setCreer(false); changed(m); }} />
      <TacheDetail id={id} onClose={() => navigate('/planification/taches')} onChanged={changed} />
    </>
  );
};

export default Taches;
